import type { Dispatch, SetStateAction } from 'react';
import { Check } from '@phosphor-icons/react';

import { ROLE_LABEL, type Role, type RoleAssignment } from '../../hooks/use-roles';
import { Icon, type IconName } from './icons';

const ROLE_ICON: Record<Role, IconName> = { admin: 'admin', direction: 'admin', teacher: 'teacher', tutor: 'student', eleve: 'student', ai_operator: 'admin' };

function assignmentKey(a: Pick<RoleAssignment, 'role' | 'schoolId'>): string {
  return `${a.role}:${a.schoolId ?? ''}`;
}

/** Libellé affiché : badge d'école quand l'utilisateur a ce rôle sur plusieurs écoles. */
function assignmentLabel(a: RoleAssignment, assignments: RoleAssignment[]): string {
  const sameRoleCount = assignments.filter((x) => x.role === a.role).length;
  if (!a.schoolName || sameRoleCount <= 1) return ROLE_LABEL[a.role];
  return `${ROLE_LABEL[a.role]} — ${a.schoolName}`;
}

export function RailRoleSwitcher({
  assignments,
  activeAssignment,
  open,
  setOpen,
  onSwitch,
}: {
  assignments: RoleAssignment[];
  activeAssignment: RoleAssignment | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSwitch: (a: RoleAssignment) => void;
}) {
  if (assignments.length <= 1 || !activeAssignment) return null;
  return (
    <div className="relative">
      {open && (
        <>
          <div onClick={() => setOpen(false)} className="fixed inset-0 z-[1]" />
          <div role="menu" className="absolute bottom-[calc(100%+6px)] left-0 right-0 z-[2] bg-[var(--rail-bg)] border border-[var(--rail-line)] rounded-[var(--naia-r-card)] shadow-[0_8px_32px_rgba(0,0,0,0.30)] p-1.5 flex flex-col gap-0.5">
            <div className="text-[9.5px] font-extrabold tracking-[.12em] text-[var(--rail-text-muted)] px-2 py-1 uppercase">CHANGER DE MODE</div>
            {assignments.map((a) => {
              const active = assignmentKey(a) === assignmentKey(activeAssignment);
              return (
                <button
                  key={assignmentKey(a)}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => onSwitch(a)}
                  className={`flex items-center gap-2.5 w-full px-2.5 py-2 min-h-[44px] rounded-[var(--naia-r-sm)] border-none cursor-pointer font-[inherit] text-[13.5px] font-semibold text-left transition-colors${active ? ' bg-[var(--rail-active)] text-[var(--rail-text-active)]' : ' bg-transparent text-[var(--rail-text)] hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)]'}`}
                >
                  <span aria-hidden className="naia-item__icon"><Icon name={ROLE_ICON[a.role]} /></span>
                  <span className="flex-1">{assignmentLabel(a, assignments)}</span>
                  {active && <Check size={14} weight="bold" aria-hidden className="shrink-0 text-[var(--rail-accent)]" />}
                </button>
              );
            })}
          </div>
        </>
      )}
      <button
        type="button"
        className="naia-roleswitch__toggle flex items-center gap-2.5 w-full px-2.5 py-2 min-h-[44px] rounded-[9px] border border-[var(--rail-line)] bg-[var(--rail-surface)] text-[var(--rail-text)] font-[inherit] text-[13.5px] font-semibold cursor-pointer text-left mb-1 hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)] transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span aria-hidden className="naia-item__icon"><Icon name={ROLE_ICON[activeAssignment.role]} /></span>
        <span className="naia-rail__label flex-1 min-w-0">
          <span className="block text-[9.5px] font-bold text-[var(--rail-text-active)] leading-[1.2]">Mode</span>
          <span className="block whitespace-nowrap overflow-hidden text-ellipsis text-[var(--rail-text-active)]">{assignmentLabel(activeAssignment, assignments)}</span>
        </span>
        <span aria-hidden className="naia-rail__label shrink-0 opacity-40 text-[var(--rail-text-active)]">⇅</span>
      </button>
    </div>
  );
}
